/**
 * SummaryTab Component
 * Displays the overview/summary of test results
 */
import React from 'react';
import { ResultData } from '../../types/results';
import { formatTime, formatDateTimeFr } from '../../utils/formatters';

interface SummaryTabProps {
  results: ResultData;
}

export const SummaryTab: React.FC<SummaryTabProps> = ({ results }) => {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-bold text-gray-800">Vue d'ensemble</h2>
        <p className="text-sm text-gray-500">Récapitulatif de la tentative</p>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Session Info */}
        <InfoCard
          title="Session"
          borderColor="border-primary"
          titleColor="text-primary"
        >
          <InfoRow label="Tentative" value={`#${results.attempt_id}`} />
          <InfoRow label="Version" value={results.version} />
          <InfoRow label="Statut" value={results.status === 'completed' ? 'Terminé' : results.status} />
          <InfoRow label="Debut" value={formatDateTimeFr(results.started_at)} />
          <InfoRow label="Fin" value={formatDateTimeFr(results.completed_at)} />
        </InfoCard>

        {/* Score Stats */}
        <InfoCard
          title="Performance"
          borderColor="border-green-500"
          titleColor="text-green-700"
        >
          <InfoRow label="Score" value={`${results.total_score} / ${results.max_score} pts`} />
          <InfoRow label="Taux" value={`${results.percentage}%`} />
          <InfoRow 
            label="Correctes" 
            value={results.correct_answers.toString()} 
            valueColor="text-green-600" 
          />
          <InfoRow 
            label="Incorrectes" 
            value={results.incorrect_answers.toString()} 
            valueColor="text-red-600" 
          />
          <InfoRow label="Passees" value={results.skipped_answers.toString()} />
        </InfoCard>

        {/* Time Stats */}
        <InfoCard
          title="Temps"
          borderColor="border-secondary"
          titleColor="text-secondary"
        >
          <InfoRow label="Total" value={results.total_time_formatted} />
          <InfoRow label="Réponses" value={formatTime(results.total_answer_time)} />
          <InfoRow label="Consignes" value={formatTime(results.total_instruction_time)} />
          <InfoRow label="Règles" value={formatTime(results.total_pre_rule_time)} />
          <InfoRow label="Moy/question" value={`${results.average_answer_time}s`} />
        </InfoCard>

        {/* Distraction Stats */}
        <InfoCard
          title="Distraction"
          borderColor="border-yellow-500"
          titleColor="text-yellow-700"
        >
          <InfoRow label="Temps cumule" value={formatTime(results.total_distraction_time)} />
          <InfoRow label="Mouvements" value={results.total_distraction_moves.toString()} />
        </InfoCard>
      </div>
    </div>
  );
};

// Sub-components
interface InfoCardProps {
  title: string;
  borderColor: string;
  titleColor: string;
  children: React.ReactNode;
}

const InfoCard: React.FC<InfoCardProps> = ({ 
  title, 
  borderColor, 
  titleColor, 
  children 
}) => (
  <div className={`bg-gray-50 p-4 rounded-lg border-l-4 ${borderColor}`}>
    <h3 className={`font-semibold text-sm ${titleColor} mb-2`}>{title}</h3>
    <div className="space-y-1 text-sm">{children}</div>
  </div>
);

interface InfoRowProps {
  label: string;
  value: string;
  valueColor?: string;
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value, valueColor }) => (
  <p>
    <span className="text-gray-500">{label}:</span>{' '}
    <span className={valueColor}>{value}</span>
  </p>
);

export default SummaryTab;
